import { apiError } from "../../../../../../utils/formErrors";
import React, { useCallback, useEffect, useState } from "react";
import { Box, Stack, Dialog, TextField, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TopBar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import DataTable from "../../../../../../component/DataTable";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import { StatusChip } from "../../../../../../component/listing";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { getVendors, createVendor, updateVendor, deactivateVendor } from "../../../../../../services/asset";
import { tokens, displayFont } from "../../../../../../theme";

/**
 * Suppliers an asset was bought from or is serviced by.
 *
 * Only the name is required. A vendor is usually logged at the moment someone
 * is recording a laptop, and demanding a full postal address then is how the
 * field ends up full of "n/a".
 *
 * There is no delete. A retired supplier is deactivated: assets already bought
 * from them keep the link, and the vendor simply stops being offered for new
 * ones. Deleting would leave those assets pointing at nothing.
 */

const Label = ({ children }) => (
  <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>{children}</Box>
);

const EMPTY = { name: "", phone: "", email: "", street: "", city: "", state: "", zip: "", country: "", notes: "" };

const Vendors = () => {
  const notify = useNotify();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const res = await getVendors();
      if (res?.success === false) throw apiError(res);
      setVendors(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(true); notify.error(e?.message || "Couldn't load vendors");
    } finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (v) => {
    setEditing(v._id);
    setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, v[k] || ""])) });
    setDialog(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return notify.error("A name is required");
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]));
      const res = editing ? await updateVendor(editing, payload) : await createVendor(payload);
      if (res?.success === false) throw apiError(res);
      notify.success(editing ? "Vendor updated" : "Vendor added");
      setDialog(false); load();
    } catch (e) {
      notify.error(e?.message || "Couldn't save the vendor");
    } finally { setBusy(false); }
  };

  const retire = async (v) => {
    try {
      const res = await deactivateVendor(v._id);
      if (res?.success === false) throw apiError(res);
      // the API says how many assets still point here — worth repeating rather
      // than a bare "done"
      notify.success(res?.note || "Vendor deactivated");
      load();
    } catch (e) {
      notify.error(e?.message || "Couldn't deactivate");
    }
  };

  const addressOf = (v) => [v.street, v.city, v.state, v.zip, v.country].filter(Boolean).join(", ");

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading="Vendors" breadcrumbs={[{ label: "Asset Tracker" }, { label: "Vendors" }]} />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Suppliers</Box>
          <BtnPrimary onClick={openNew} sx={{ borderRadius: "8px" }}>Add vendor</BtnPrimary>
        </Stack>

        <DataTable
          rows={vendors}
          loading={loading}
          error={error}
          onRetry={load}
          errorTitle="Couldn't load vendors"
          empty={{
            title: "No vendors yet",
            description: "Add the suppliers you buy equipment from, so an asset can record where it came from.",
          }}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            {
              key: "name", label: "Name",
              render: (v) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ fontWeight: 600 }}>{v.name}</Box>
                  {v.isActive === false && <StatusChip label="Retired" tone="muted" />}
                </Stack>
              ),
            },
            {
              key: "contact", label: "Contact", cellSx: { color: tokens.textMuted },
              render: (v) => [v.phone, v.email].filter(Boolean).join(" · ") || "—",
            },
            {
              key: "address", label: "Address", cellSx: { color: tokens.textMuted },
              render: (v) => addressOf(v) || "—",
            },
            {
              key: "actions", label: <>&nbsp;</>, width: "13rem", align: "right",
              render: (v) => (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <BtnSecondary onClick={(e) => { e.stopPropagation(); openEdit(v); }} sx={{ borderRadius: "8px", fontSize: "0.8125rem" }}>Edit</BtnSecondary>
                  {v.isActive !== false && (
                    <BtnSecondary onClick={(e) => { e.stopPropagation(); retire(v); }} sx={{ borderRadius: "8px", fontSize: "0.8125rem" }}>Retire</BtnSecondary>
                  )}
                </Stack>
              ),
            },
          ]}
        />
      </MainBox>

      <Dialog open={dialog} onClose={busy ? undefined : () => setDialog(false)} PaperProps={{ sx: { borderRadius: "12px", width: "min(34rem,94vw)" } }}>
        <Box sx={{ p: "1.5rem" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
            <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{editing ? "Edit vendor" : "Add vendor"}</Box>
            <IconButton aria-label="Close" size="small" onClick={() => setDialog(false)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>

          <Label>Name</Label>
          <TextField fullWidth size="small" value={form.name} placeholder="e.g. Dell India"
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} sx={{ mb: "1rem" }} />

          <Stack direction="row" spacing={1.25} sx={{ mb: "1rem" }}>
            <Box sx={{ flex: 1 }}>
              <Label>Phone</Label>
              <TextField fullWidth size="small" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Label>Email</Label>
              <TextField fullWidth size="small" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </Box>
          </Stack>

          {/* optional, and labelled so, because the model does not require it and
              a form that insists fills the field with placeholder text */}
          <Label>Address (optional)</Label>
          <TextField fullWidth size="small" placeholder="Street" value={form.street}
            onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))} sx={{ mb: "0.5rem" }} />
          <Stack direction="row" spacing={1} sx={{ mb: "0.5rem" }}>
            <TextField size="small" placeholder="City" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} sx={{ flex: 1 }} />
            <TextField size="small" placeholder="State" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} sx={{ flex: 1 }} />
            <TextField size="small" placeholder="ZIP" value={form.zip} onChange={(e) => setForm((f) => ({ ...f, zip: e.target.value }))} sx={{ width: "6rem" }} />
          </Stack>
          <TextField fullWidth size="small" placeholder="Country" value={form.country}
            onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} sx={{ mb: "1rem" }} />

          <Label>Notes (optional)</Label>
          <TextField fullWidth size="small" multiline minRows={2} value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} sx={{ mb: "1.5rem" }} />

          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            <BtnSecondary onClick={() => setDialog(false)} disabled={busy} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
            <BtnPrimary onClick={submit} disabled={busy} sx={{ borderRadius: "8px" }}>
              {busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editing ? "Save changes" : "Add vendor"}
            </BtnPrimary>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Vendors;
